import apiClient from './apiClient'

/**
 * Starts a video upload. The backend validates the file synchronously, then transcodes it in the
 * background and returns `202 { jobId, status: 'processing' }`. Poll `getVideoJobStatus(jobId)` for the
 * terminal result and the final `videoSrc`. The target page is derived from the access token, so no id
 * is sent.
 *
 * @param {FormData} formData - must contain a single `file` entry
 * @returns {Promise<{ jobId: string, status: string }>}
 * @throws the axios error on a *synchronous* rejection; read `err.response.data.code`
 *         (VIDEO_TOO_LARGE, VIDEO_TOO_LONG, INVALID_FILE_TYPE, NO_VIDEO_STREAM, UPLOAD_IN_PROGRESS).
 *         Processing failures (PROCESSING_FAILED, PROCESSING_TIMEOUT) surface via getVideoJobStatus.
 */
export async function uploadNewVideo(formData) {
    const { data } = await apiClient.post('/v1/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
}

/**
 * Polls the status of an async video job.
 *
 * @param {string} jobId - the id returned by `uploadNewVideo`
 * @returns {Promise<{ status: 'processing'|'ready'|'failed', videoSrc: ?string, code: ?string }>}
 *          `videoSrc` is set once `ready`; `code` is a VideoErrorCode name when `failed`.
 * @throws the axios error; a 404 means the job is unknown (e.g. lost to a server restart).
 */
export async function getVideoJobStatus(jobId) {
    const { data } = await apiClient.get(`/v1/videos/jobs/${jobId}`)
    return data
}
